import React from 'react'
import {Loading, Pagination, Tabs} from '@therms/atalaya'
import {useCurrentEventCounts} from '@src/modules/Events/hooks/useCurrentEventCounts'
import {
  arrayValuesOrUndefined,
  EventListCard,
  NoResults,
  RPCDocs,
  useEvents,
  useLocationContext,
  usePaginationState,
  useRPCServerEventHandler,
  useTabsState,
} from '@therms/react-modules'
import {MdCheckBoxOutlineBlank} from 'react-icons/md'
import {useUpdateEffect} from 'ahooks'

interface EventsTabsLayoutProps {
  activityTypeIds?: string[]
  dateTimeRange: {start?: string; end?: string}
  eventTypeIds?: string[]
  locationId?: string
  onSelectEventId: (eventId: string) => void
  searchText?: string
  userIdsAssigned?: string[]
}

function EventsTabsLayout({
  activityTypeIds,
  dateTimeRange,
  eventTypeIds,
  locationId,
  onSelectEventId,
  searchText,
  userIdsAssigned,
}: EventsTabsLayoutProps) {
  const locationCtx = useLocationContext()

  const [activeTabKey, setActiveTabKey] = useTabsState(
    'evtstb',
    RPCDocs.Events.GetEventsList.v1.StateFilter.Closed,
  )

  const {recordsPerPage, setRecordsPerPage, setSkippedRecords, skippedRecords} =
    usePaginationState('evntclsdlist', [])

  const {counts} = useCurrentEventCounts({
    activityTypeIdFilter: arrayValuesOrUndefined(activityTypeIds),
    assignedUserIdFilter: arrayValuesOrUndefined(userIdsAssigned),
    eventTypeIds: arrayValuesOrUndefined(eventTypeIds),
    fromDate: dateTimeRange.start,
    locationId: locationCtx?.locationId ?? locationId,
    toDate: dateTimeRange.end,
  })

  const {data, loading, refresh} = useEvents({
    activityTypeIdFilter: activityTypeIds?.length ? activityTypeIds : undefined,
    assignedUserIdFilter: userIdsAssigned,
    eventTypeIds: eventTypeIds?.length ? eventTypeIds : undefined,
    fromDate: dateTimeRange?.start,
    locationId: locationCtx?.locationId ?? locationId,
    pagination: {
      limit: recordsPerPage,
      skip: skippedRecords,
    },
    searchText,
    stateFilter: [activeTabKey],
    toDate: dateTimeRange?.end,
  })

  useUpdateEffect(() => {
    setSkippedRecords(0)
  }, [
    activityTypeIds,
    dateTimeRange,
    eventTypeIds,
    locationId,
    searchText,
    userIdsAssigned,
  ])

  const handleEventClicked = (
    event:
      | RPCDocs.Events.GetEventsList.v1.Event
      | RPCDocs.Events.GetEvent.v2.Event,
  ) => {
    onSelectEventId(event.id)
  }

  useRPCServerEventHandler('EventCreated', refresh)
  useRPCServerEventHandler('EventDeleted', refresh)
  useRPCServerEventHandler('EventDetailsUpdated', refresh)
  useRPCServerEventHandler('EventStateUpdated', refresh)

  return (
    <div>
      <Tabs
        currentKey={activeTabKey}
        onTabClick={(newKey) => {
          setActiveTabKey(newKey)
          setSkippedRecords(0)
        }}
      >
        <Tabs.Tab
          notifications={counts.pending}
          tabKey={RPCDocs.Events.GetEventsList.v1.StateFilter.Pending}
          title="Pending"
        />

        <Tabs.Tab
          notifications={counts.active}
          tabKey={RPCDocs.Events.GetEventsList.v1.StateFilter.Active}
          title="Active"
        />

        <Tabs.Tab
          tabKey={RPCDocs.Events.GetEventsList.v1.StateFilter.Closed}
          title="Closed"
        />
      </Tabs>

      {loading && !data?.events?.length && (
        <div className="mt-xl flex justify-center">
          <Loading message="Loading Events" messagePosition="vertical" />
        </div>
      )}

      {!loading && !!data?.events?.length && (
        <div className="space-y-xs">
          {data?.events?.map((event) => (
            <EventListCard
              event={event}
              key={event.id}
              onClick={handleEventClicked}
            />
          ))}
        </div>
      )}

      {!loading && !data?.events?.length && (
        <NoResults text="No Events" iconName={MdCheckBoxOutlineBlank} />
      )}

      <div className="my-base">
        <Pagination
          onPageClick={setSkippedRecords}
          onSetRecordsPerPage={setRecordsPerPage}
          recordsPerPage={recordsPerPage}
          showDescription
          skippedRecords={skippedRecords}
          totalRecords={data?.total || 0}
        />
      </div>
    </div>
  )
}

export {EventsTabsLayout}
